/**
 * Signal hooks — typed TanStack Query wrappers for the signal engine.
 *
 * Replaces: js/modules/sentinel.js, js/modules/scanner.js (partial)
 * Backend:  backend/market/interfaces.py
 */

import { useQuery } from "@tanstack/react-query";

import { apiGet } from "@/lib/api-client";
import type {
  SignalSummary,
  SignalsSummaryResponse,
  ContrarianOpportunity,
} from "@/sdk/types/market";

export const SIGNAL_QUERY_KEYS = {
  summary: ["signals", "summary"] as const,
  contrarian: ["signals", "contrarian"] as const,
  momentum: ["signals", "momentum"] as const,
  whaleFlow: ["signals", "whale-flow"] as const,
  volume: ["signals", "volume"] as const,
};

/**
 * Flatten the backend summary envelope. The live backend never returns the
 * flat count fields — they must be derived from signal_counts, whose values
 * are {direction, count} records (backend/services/signal_engine.py).
 * "volume_spike" is the backend enum value for volume anomalies. Payloads
 * already carrying the flat fields (legacy shape) pass through unchanged.
 */
export function toSignalSummary(
  raw: SignalsSummaryResponse | SignalSummary,
): SignalSummary {
  if ("signal_counts" in raw && raw.signal_counts && typeof raw.signal_counts === "object") {
    const counts = raw.signal_counts;
    const countFor = (signalType: string) => Number(counts[signalType]?.count ?? 0) || 0;
    const total = Object.values(counts).reduce(
      (sum, entry) => sum + (Number(entry?.count) || 0),
      0,
    );
    return {
      total_signals: total,
      contrarian_count: countFor("contrarian"),
      momentum_count: countFor("momentum"),
      whale_flow_count: countFor("whale_flow"),
      volume_anomaly_count: countFor("volume_spike"),
      timestamp: raw.timestamp,
    };
  }
  const flat = raw as Partial<SignalSummary>;
  return {
    total_signals: Number(flat.total_signals ?? 0) || 0,
    contrarian_count: Number(flat.contrarian_count ?? 0) || 0,
    momentum_count: Number(flat.momentum_count ?? 0) || 0,
    whale_flow_count: Number(flat.whale_flow_count ?? 0) || 0,
    volume_anomaly_count: Number(flat.volume_anomaly_count ?? 0) || 0,
    timestamp: String(flat.timestamp ?? ""),
  };
}

export function useSignalsSummary(refetchInterval = 20_000) {
  return useQuery({
    queryKey: SIGNAL_QUERY_KEYS.summary,
    queryFn: () =>
      apiGet<SignalsSummaryResponse | SignalSummary>("/api/signals/summary").then(
        toSignalSummary,
      ),
    refetchInterval,
  });
}

export function useContrarianSignals(limit = 50, refetchInterval = 20_000) {
  return useQuery({
    queryKey: SIGNAL_QUERY_KEYS.contrarian,
    queryFn: () =>
      apiGet<{ opportunities: ContrarianOpportunity[] }>(
        `/api/signals/contrarian?limit=${limit}`,
      ),
    refetchInterval,
  });
}

export function useMomentumSignals(refetchInterval = 20_000) {
  return useQuery({
    queryKey: SIGNAL_QUERY_KEYS.momentum,
    queryFn: () =>
      apiGet<{ signals: unknown[] }>("/api/signals/momentum"),
    refetchInterval,
  });
}

export function useWhaleFlowSignals(refetchInterval = 20_000) {
  return useQuery({
    queryKey: SIGNAL_QUERY_KEYS.whaleFlow,
    queryFn: () =>
      apiGet<{ whale_trades: unknown[] }>("/api/signals/whale-flow"),
    refetchInterval,
  });
}

export function useVolumeSignals(refetchInterval = 20_000) {
  return useQuery({
    queryKey: SIGNAL_QUERY_KEYS.volume,
    queryFn: () => apiGet<{ anomalies: unknown[] }>("/api/signals/volume"),
    refetchInterval,
  });
}
